import { Avatar, Group, Image, Stack, Text } from '@mantine/core'
import { IconPhotoScan } from '@tabler/icons-react'
import { useMemo } from 'react'

import avatars from '@/assets/avatars'
import { CollapsibleContentSection } from '@/components/CollapsibleContentSection'
import { EmptyState } from '@/components/EmptyState'
import { TableContentGrid } from '@/components/TableContentGrid'
import { useAllEsks } from '@/hooks/useAllEsks'
import { useGlobalState } from '@/hooks/useGlobalState'
import { useTopicsEnabled } from '@/hooks/useTopicsEnabled'
import { getDefaultPskAvatar } from '@/screens/SetupScreen/utils'
import { isHelpModule } from '@/utils/contentModuleTables'

import { useContentModuleScreenData } from './ContentModuleScreen'

export function ModuleSection() {
  const { configStore } = useGlobalState()
  const data = useContentModuleScreenData()
  const { topicsEnabled } = useTopicsEnabled()
  const { allEsks, isReady: isEskDataReady, sidekicksQuery } = useAllEsks()

  const eskName = useMemo(() => {
    // For MSK content modules, always show "Personal Sidekick"
    if (data.cmQuery.data?.visibleToMsk) {
      return 'Personal Sidekick'
    }

    // For ESK content modules, find the ESK by selectEsk
    const selectEsk = data.cmQuery.data?.selectEsk
    if (selectEsk) {
      // Handle PSK case - show "Personal Sidekick" instead of "Avatar PSK"
      if (selectEsk === 'PSK') {
        return 'Personal Sidekick'
      }

      // Return loading placeholder if ESK data isn't ready yet
      if (!isEskDataReady) {
        return ''
      }

      const esk = allEsks.find(e => e.eskId === selectEsk)
      return esk?.name ?? `Avatar ${selectEsk}`
    }

    return 'No Sidekick Selected'
  }, [data.cmQuery.data?.selectEsk, data.cmQuery.data?.visibleToMsk, allEsks, isEskDataReady])

  const eskAvatar = useMemo(() => {
    // For MSK content modules, use the default PSK avatar
    if (data.cmQuery.data?.visibleToMsk) {
      return getDefaultPskAvatar(sidekicksQuery.data)
    }

    // For ESK content modules, find the ESK by selectEsk
    const selectEsk = data.cmQuery.data?.selectEsk
    if (!selectEsk) return undefined

    const esk = allEsks.find(e => e.eskId === selectEsk)
    return esk ? avatars[esk.avatarId] : undefined
  }, [data.cmQuery.data?.selectEsk, data.cmQuery.data?.visibleToMsk, allEsks, sidekicksQuery.data])

  const rows = useMemo(() => {
    const baseRows = [
      { heading: 'Title', content: <Text fw={600}>{data.cmQuery.data?.title}</Text> },
      {
        heading: 'Owner(s)',
        content: (
          <Stack gap={2}>
            <Text fw={500}>{data.cmQuery.data?.owner || 'Unknown'}</Text>
            {data.cmQuery.data?.additionalOwners?.map(ao => (
              <Text key={ao.userId} size="sm" c="dimmed">
                {ao.name || ao.email || ao.userId}
              </Text>
            ))}
          </Stack>
        ),
      },
      {
        heading: 'Sidekick',
        content: (
          <Group align="center">
            <Avatar src={eskAvatar} variant={!!eskAvatar ? 'transparent' : 'light'} radius="sm" size="lg" />
            <Text>{eskName}</Text>
          </Group>
        ),
      },
      {
        heading: 'Module Description',
        content: <Text style={{ whiteSpace: 'pre-wrap' }}>{data.cmQuery.data?.shortDescription}</Text>,
      },
      // Topic only shown when topics are enabled and module is not a help module (help modules don't get topics)
      ...(topicsEnabled && data.cmQuery.data && !isHelpModule(data.cmQuery.data)
        ? [
            {
              heading: 'Topic',
              content: (
                <Text c={data.cmQuery.data?.categories?.length ? undefined : 'dimmed'}>
                  {data.cmQuery.data?.categories?.length
                    ? data.cmQuery.data.categories.join(', ')
                    : 'No topic assigned'}
                </Text>
              ),
            },
          ]
        : []),
      {
        heading: 'Target Audience',
        content: <Text style={{ whiteSpace: 'pre-wrap' }}>{data.cmQuery.data?.demographic || 'Not specified'}</Text>,
      },
      {
        heading: 'Module Introduction',
        content: <Text style={{ whiteSpace: 'pre-wrap' }}>{data.cmQuery.data?.introduction || 'Not specified'}</Text>,
      },
      {
        heading: 'Module Intro Image',
        content: data.cmQuery.data?.titleImage ? (
          <Image
            fit="contain"
            radius="md"
            h="auto"
            mah="300px"
            w="auto"
            maw="100%"
            src={`${configStore.serverBaseUrl}/files/${data.cmQuery.data?.titleImage}`}
          />
        ) : (
          <EmptyState size="sm" Icon={IconPhotoScan} title="No Intro Image Uploaded" />
        ),
      },
    ]

    return baseRows
  }, [data.cmQuery.data, eskName, eskAvatar, configStore.serverBaseUrl, topicsEnabled])

  return (
    <CollapsibleContentSection
      id="module-details"
      title="Module Details"
      subtitle="General module information and user experience settings."
      ContentElement={<TableContentGrid headingFlexWidth="245px" rows={rows} />}
    />
  )
}
